import ExcelJS from "exceljs";
import { DashboardData, DashboardFilters } from "./types";

export type DashboardExportFormat = "csv" | "pdf" | "json" | "xlsx";

export interface DashboardExportPayload {
  filters: DashboardFilters;
  dataPeriod: DashboardData["dataPeriod"];
  refreshedAt: string | null;
  overview: DashboardData["overview"];
  recordsExtracted: DashboardData["recordsExtracted"];
  reviewerWork: DashboardData["reviewerWork"];
  qualitySampling: DashboardData["qualitySampling"];
  correctiveActions: DashboardData["correctiveActions"];
}

export interface DashboardExportRow {
  section: string;
  metric: string;
  value: string | number;
}

const screeningFamilyLabels: Record<string, string> = {
  ALL: "All risk areas and queues",
  PV_PTQ: "PV + PTQ",
  PCO: "PCO",
};

const percentage = (value: number | null) =>
  value === null ? "Not available" : `${value.toFixed(1)}%`;

export const buildDashboardExportPayload = ({
  data,
  filters,
}: {
  data: DashboardData;
  filters: DashboardFilters;
}): DashboardExportPayload => ({
  filters,
  dataPeriod: data.dataPeriod,
  refreshedAt: data.refreshedAt,
  overview: data.overview,
  recordsExtracted: data.recordsExtracted,
  reviewerWork: data.reviewerWork,
  qualitySampling: data.qualitySampling,
  correctiveActions: data.correctiveActions,
});

export const buildDashboardExportRows = ({
  payload,
}: {
  payload: DashboardExportPayload;
}): DashboardExportRow[] => {
  const extracted = payload.recordsExtracted;
  const aiFlaggedRate = extracted.recordsExtracted
    ? (extracted.aiFlaggedRecords / extracted.recordsExtracted) * 100
    : null;

  return [
    {
      section: "Filters",
      metric: "Date range",
      value: `${payload.dataPeriod.startDate} to ${payload.dataPeriod.endDate}`,
    },
    {
      section: "Filters",
      metric: "Region",
      value: payload.filters.regions.join(", ") || "ALL",
    },
    {
      section: "Filters",
      metric: "Country",
      value: payload.filters.countries.join(", ") || "ALL",
    },
    {
      section: "Filters",
      metric: "Risk area or screening queue",
      value:
        screeningFamilyLabels[payload.filters.screeningFamily] ??
        payload.filters.screeningFamily,
    },
    {
      section: "Filters",
      metric: "Last refreshed",
      value: payload.refreshedAt ?? "Not available",
    },
    {
      section: "Overview",
      metric: "Records extracted",
      value: payload.overview.recordsExtracted,
    },
    {
      section: "Overview",
      metric: "AI-flagged records",
      value: payload.overview.aiFlaggedRecords,
    },
    {
      section: "Overview",
      metric: "Reviews submitted",
      value: payload.overview.reviewsSubmitted,
    },
    {
      section: "Overview",
      metric: "Sample pass rate",
      value: percentage(payload.overview.samplePassRate),
    },
    {
      section: "Records Extracted",
      metric: "Records extracted",
      value: extracted.recordsExtracted,
    },
    {
      section: "Records Extracted",
      metric: "No flag",
      value: extracted.recordsNotFlagged,
    },
    {
      section: "Records Extracted",
      metric: "AI-flagged records",
      value: extracted.aiFlaggedRecords,
    },
    {
      section: "Records Extracted",
      metric: "AI-flagged rate",
      value: percentage(aiFlaggedRate),
    },
    {
      section: "Records Extracted",
      metric: "QC Fail",
      value: extracted.flaggedRecordsQcFail,
    },
    {
      section: "Records Extracted",
      metric: "QC Pass",
      value: extracted.flaggedRecordsQcPass,
    },
    {
      section: "Records Extracted",
      metric: "Awaiting review",
      value: extracted.flaggedRecordsAwaitingReview,
    },
    {
      section: "Reviewer Work Done",
      metric: "Reviews submitted",
      value: payload.reviewerWork.reviewsSubmitted,
    },
    {
      section: "Reviewer Work Done",
      metric: "QC Pass",
      value: payload.reviewerWork.qcPassEvaluations,
    },
    {
      section: "Reviewer Work Done",
      metric: "QC Fail",
      value: payload.reviewerWork.qcFailEvaluations,
    },
    {
      section: "Reviewer Work Done",
      metric: "Countries with reviews",
      value: payload.reviewerWork.countriesWithReviews,
    },
    ...payload.reviewerWork.reviewsByCountry.map((country) => ({
      section: "Reviews by country",
      metric: country.country,
      value: country.reviewsSubmitted,
    })),
    ...payload.reviewerWork.reviewsSubmittedByWeek.flatMap((week) => [
      {
        section: "Reviews submitted by week",
        metric: `${week.weekStart} PV + PTQ`,
        value: week.pvPtq,
      },
      {
        section: "Reviews submitted by week",
        metric: `${week.weekStart} PCO`,
        value: week.pco,
      },
    ]),
    ...payload.reviewerWork.qcFailRateByWeek.flatMap((week) => [
      {
        section: "QC Fail rate by week",
        metric: `${week.weekStart} PV + PTQ`,
        value: percentage(week.pvPtq),
      },
      {
        section: "QC Fail rate by week",
        metric: `${week.weekStart} PCO`,
        value: percentage(week.pco),
      },
    ]),
    {
      section: "Quality & Sampling Work Done",
      metric: "Completed samples",
      value: payload.qualitySampling.completedSamples,
    },
    {
      section: "Quality & Sampling Work Done",
      metric: "Unevaluated samples",
      value: payload.qualitySampling.unevaluatedSamples,
    },
    {
      section: "Quality & Sampling Work Done",
      metric: "Countries represented",
      value: payload.qualitySampling.countriesRepresented,
    },
    {
      section: "Quality & Sampling Work Done",
      metric: "Sample pass rate",
      value: percentage(payload.qualitySampling.samplePassRate),
    },
    ...payload.qualitySampling.completedSamplesByCountry.map((country) => ({
      section: "Completed samples by country",
      metric: country.country,
      value: country.completedSamples,
    })),
    {
      section: "Corrective Actions Work Done",
      metric: "QC Fail evaluations",
      value: payload.correctiveActions.qcFailEvaluations,
    },
    {
      section: "Corrective Actions Work Done",
      metric: "Notifications sent",
      value: payload.correctiveActions.notificationsSent,
    },
    {
      section: "Corrective Actions Work Done",
      metric: "Acknowledged",
      value: payload.correctiveActions.acknowledged,
    },
    ...payload.correctiveActions.qcFailEvaluationsByScreening.map(
      (screening) => ({
        section: "QC Fail evaluations by screening",
        metric:
          screeningFamilyLabels[screening.screeningFamily] ??
          screening.screeningFamily,
        value: screening.evaluations,
      }),
    ),
  ];
};

const escapeCsv = (value: string | number) => {
  const text = String(value);
  const safeText =
    typeof value === "string" && /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safeText)
    ? `"${safeText.replace(/"/g, '""')}"`
    : safeText;
};

export const dashboardRowsToCsv = ({ rows }: { rows: DashboardExportRow[] }) =>
  [
    ["Section", "Metric", "Value"],
    ...rows.map((row) => [row.section, row.metric, row.value]),
  ]
    .map((row) => row.map(escapeCsv).join(","))
    .join("\r\n");

const worksheetNameOverrides: Record<string, string> = {
  "QC Fail evaluations by screening": "QC Fail evals by screening",
};

const worksheetNameFor = (section: string) =>
  worksheetNameOverrides[section] ?? section.slice(0, 31);

const excelSafeValue = (value: string | number) =>
  typeof value === "string" && /^[=+\-@]/.test(value) ? `'${value}` : value;

export const buildDashboardWorkbook = ({
  rows,
}: {
  rows: DashboardExportRow[];
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Text Sentinel";

  Array.from(new Set(rows.map((row) => row.section))).forEach((section) => {
    const worksheet = workbook.addWorksheet(worksheetNameFor(section));
    worksheet.columns = [
      { header: "Metric", key: "metric", width: 42 },
      { header: "Value", key: "value", width: 24 },
    ];
    worksheet.views = [{ state: "frozen", ySplit: 1 }];
    worksheet.autoFilter = "A1:B1";

    rows
      .filter((row) => row.section === section)
      .forEach((row) =>
        worksheet.addRow({
          metric: excelSafeValue(row.metric),
          value: excelSafeValue(row.value),
        }),
      );

    const header = worksheet.getRow(1);
    header.font = { bold: true, color: { argb: "FFFFFFFF" } };
    header.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1C359A" },
    };
  });

  return workbook;
};

const downloadBlob = ({ blob, fileName }: { blob: Blob; fileName: string }) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadDashboard = async ({
  format,
  data,
  filters,
}: {
  format: DashboardExportFormat;
  data: DashboardData;
  filters: DashboardFilters;
}) => {
  if (format === "pdf") {
    window.print();
    return;
  }

  const payload = buildDashboardExportPayload({ data, filters });
  const rows = buildDashboardExportRows({ payload });
  const baseName = `text-sentinel-dashboard-${payload.dataPeriod.endDate}`;

  if (format === "csv") {
    downloadBlob({
      blob: new Blob([dashboardRowsToCsv({ rows })], {
        type: "text/csv;charset=utf-8",
      }),
      fileName: `${baseName}.csv`,
    });
    return;
  }

  if (format === "xlsx") {
    const workbook = buildDashboardWorkbook({ rows });
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob({
      blob: new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName: `${baseName}.xlsx`,
    });
    return;
  }

  downloadBlob({
    blob: new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    }),
    fileName: `${baseName}.json`,
  });
};
